import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { partnersApi } from '@/api/endpoints'
import { Avatar, ChevronIcon, PartnersIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/utils'
import type { PartnerRow } from '@/types/api'

import { AddPartnerModal } from './AddPartnerModal'
import { PARTNER_STATUS, num, pct, usd } from './format'
import { ReportModal } from './ReportModal'
import { FigureStrip, TD, TH, THEAD_ROW, TableScroll } from './ui'

export const PARTNERS_KEY = ['partners'] as const

/**
 * Every partner: who they are, what they may discount, and the money — earned,
 * to be settled and settled. The strip on top sums all partners.
 */
export function PartnersPage() {
  const navigate = useNavigate()
  const [adding, setAdding] = useState(false)
  const [reporting, setReporting] = useState(false)
  const partners = useQuery({ queryKey: PARTNERS_KEY, queryFn: partnersApi.list })
  const rows = partners.data ?? []

  const sum = useMemo(() => {
    const add = (key: keyof PartnerRow['totals']) => rows.reduce((total, row) => total + num(row.totals?.[key]), 0)
    return {
      partners: rows.length,
      active: rows.filter((row) => row.status === 'active').length,
      users: add('usersOnboarded'),
      paid: add('paidCents'),
      earned: add('earnedCents'),
      pending: add('pendingCents'),
      settled: add('settledCents'),
      couponsActive: add('couponsActive'),
    }
  }, [rows])

  return (
    <>
      <PageHeader
        title="Partners"
        description="Partners hand out coupon codes and earn the gap between their maximum discount and each code's discount."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => setReporting(true)}>
              Report
            </Button>
            <Button size="sm" onClick={() => setAdding(true)}>
              Add partner
            </Button>
          </>
        }
      />

      <FigureStrip
        className="mb-5"
        loading={partners.isLoading}
        figures={[
          { key: 'partners', label: 'Partners', value: sum.partners, hint: `${sum.active} active · ${sum.couponsActive} live codes` },
          { key: 'users', label: 'Users onboarded', value: sum.users.toLocaleString() },
          { key: 'paid', label: 'Customers paid', value: usd(sum.paid) },
          { key: 'earned', label: 'Overall earned', value: usd(sum.earned) },
          { key: 'pending', label: 'To be settled', value: usd(sum.pending), tone: sum.pending > 0 ? 'warning' : 'neutral' },
          { key: 'settled', label: 'Settled', value: usd(sum.settled), tone: 'success' },
        ]}
      />

      <section className="card overflow-hidden">
        {partners.isLoading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        ) : partners.isError ? (
          <ErrorState title="Partners could not be loaded" onRetry={() => void partners.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<PartnersIcon className="h-7 w-7" />}
            title="No partners yet"
            description="Add a partner, give them a maximum discount, and they can sign in at partner.jobslide.app to make codes."
            action={<Button onClick={() => setAdding(true)}>Add partner</Button>}
          />
        ) : (
          <TableScroll>
            <table className="w-full min-w-[980px] text-left">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className={TH}>Partner</th>
                  <th className={TH}>Tag</th>
                  <th className={cn(TH, 'text-right')}>Max</th>
                  <th className={TH}>Status</th>
                  <th className={cn(TH, 'text-right')}>Coupons</th>
                  <th className={cn(TH, 'text-right')}>Users</th>
                  <th className={cn(TH, 'text-right')}>Earned</th>
                  <th className={cn(TH, 'text-right')}>To settle</th>
                  <th className={cn(TH, 'text-right')}>Settled</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line text-[13.5px]">
                {rows.map((row) => {
                  const status = PARTNER_STATUS[row.status] ?? PARTNER_STATUS.active
                  const t = row.totals
                  return (
                    <tr key={row.id} onClick={() => navigate(`/partners/${row.id}`)} className="group cursor-pointer transition-colors hover:bg-surface-muted">
                      <td className={TD}>
                        <Link to={`/partners/${row.id}`} onClick={(event) => event.stopPropagation()} className="flex min-w-0 items-center gap-3">
                          <Avatar name={row.name} />
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-ink">{row.name}</span>
                            <span className="block truncate text-[12.5px] text-ink-muted">{row.company || row.email}</span>
                          </span>
                        </Link>
                      </td>
                      <td className={TD}>
                        <span className="num rounded-[6px] bg-accent-soft px-1.5 py-0.5 text-[12px] font-semibold text-ink-soft">{row.tag}</span>
                      </td>
                      <td className={cn(TD, 'num text-right font-medium text-ink')}>{pct(row.maxDiscountPct)}</td>
                      <td className={TD}>
                        <StatusPill size="sm" tone={status.tone}>
                          {status.label}
                        </StatusPill>
                      </td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>
                        <span className="font-medium text-ink">{num(t?.couponsActive)}</span>
                        <span className="text-ink-faint"> / {num(t?.couponsCreated)}</span>
                      </td>
                      <td className={cn(TD, 'num text-right font-medium text-ink')}>{num(t?.usersOnboarded).toLocaleString()}</td>
                      <td className={cn(TD, 'num text-right text-ink')}>{usd(t?.earnedCents)}</td>
                      <td className={cn(TD, 'num text-right', num(t?.pendingCents) > 0 ? 'font-semibold text-warning' : 'text-ink-faint')}>{usd(t?.pendingCents)}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(t?.settledCents)}</td>
                      <td className="pr-4 text-right">
                        <ChevronIcon className="inline h-4 w-4 text-ink-faint transition-transform group-hover:translate-x-0.5" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </section>

      <AddPartnerModal open={adding} onClose={() => setAdding(false)} existingTags={rows.map((row) => row.tag)} />
      <ReportModal open={reporting} onClose={() => setReporting(false)} />
    </>
  )
}
